import { Icon } from 'Components';
import { Review, LearningPath } from 'LearningManager/pages/QuickSetup';
import {
	AssignUsers,
	Review as CustomReview,
	SelectCourses
} from 'LearningManager/pages/CustomSetup';
import course1 from 'LearningManager/images/course-1.png';
import course2 from 'LearningManager/images/course-2.png';
import course3 from 'LearningManager/images/course-3.jpg';
import course4 from 'LearningManager/images/course-4.jpg';
import React from 'react';
import Organization from 'LearningManager/components/AssignUsers/Organisation';
import Property from 'LearningManager/components/AssignUsers/Property';
import Role from 'LearningManager/components/AssignUsers/Role';
import Group from 'LearningManager/components/AssignUsers/Group';
import Individual from 'LearningManager/components/AssignUsers/Individual';
import Configure from 'LearningManager/pages/Configure';

export type AssignmentSetupType = 'Quick Setup' | 'Custom Setup';
export interface Step {
	id: number;
	title: string;
	component: React.ComponentType;
}
export interface SetupProps {
	title: string;
	icon: React.ReactNode;
	subTitle: string;
	listTitle: string;
	list: string[];
	isRecommended?: boolean;
	isActive?: boolean;
	onClick?: () => void;
}

export interface Language {
	label: string;
}

export interface Course {
	id: string;
	number: number;
	title: string;
	subtitle: string;
	description: string;
	runTime: string;
	created: string;
	languages: Language[];
}

export interface CourseDetailModalProps {
	title: string;
	courseId: string;
	description: string;
	runTime: string;
	created: string;
	languages: Language[];
	courses: Course[];
}

export const SetupOptions: SetupProps[] = [
	{
		title: 'Quick Setup',
		icon: <Icon type="quickSetupIcon" role="img" />,
		subTitle: "Get started fast with Venza's proven setup",
		listTitle: 'Default assignments:',
		list: [
			'Staff - Basic Learning Path',
			'Managers - Basic + Intermediate Learning Path',
			'Leadership & IT - Basic + Intermediate + Advanced Learning Path'
		],
		isRecommended: true
	},
	{
		title: 'Custom Setup',
		icon: <Icon type="customSetupIcon" role="img" />,
		subTitle: 'Build a tailored curriculum with full control',
		listTitle: 'You can customize:',
		list: [
			'Individual courses and learning paths',
			'Properties, groups & users',
			'Status, assignments type and more'
		]
	}
];

export const quickSetupSteps: Step[] = [
	{
		id: 1,
		title: 'Learning Path Review',
		component: LearningPath
	},
	{
		id: 2,
		title: 'Configure Settings',
		component: Configure
	},
	{ id: 3, title: 'Review & Confirm', component: Review }
];

export const customSetupSteps: Step[] = [
	{ id: 1, title: 'Select Courses', component: SelectCourses },
	{ id: 2, title: 'Assign Users', component: AssignUsers },
	{
		id: 3,
		title: 'Configure Settings',
		component: Configure
	},
	{ id: 4, title: 'Review & Confirm', component: CustomReview }
];

export const getStepsByType = (type: AssignmentSetupType): Step[] =>
	type === 'Quick Setup' ? quickSetupSteps : customSetupSteps;

export const learningPaths = [
	{
		title: 'Basic Learning Path',
		duration: '20 min',
		description:
			'Training that dives deeper into information security, addressing organizational-level challenges, including data breaches, policies, vendor management, and more.',
		courses: [
			{
				id: 1,
				title: 'All About Data',
				tag: 'InfoSec',
				image: course1
			},
			{
				id: 2,
				title: 'Data Threats',
				tag: 'InfoSec',
				image: course2
			},
			{
				id: 3,
				title: 'Protecting Data',
				tag: 'InfoSec',
				image: course3
			}
		]
	},
	{
		title: 'Intermediate Learning Path',
		duration: '40 min',
		description:
			'Deeper training covering breaches, policies, and organizational challenges.',
		courses: [
			{
				id: 1,
				title: 'Information Security Breach',
				tag: 'InfoSec',
				image: course4
			},
			{
				id: 2,
				title: 'Booking Hotel Scam',
				tag: 'InfoSec',
				image: course3
			},
			{
				id: 3,
				title: 'Urgent CEO Request',
				tag: 'InfoSec',
				image: course2
			},
			{
				id: 4,
				title: 'Urgent CEO Request',
				tag: 'InfoSec',
				image: course1
			}
		]
	},
	{
		title: 'Advanced Learning Path',
		duration: '15 min',
		description:
			'Training to guide company leadership on how to conceptualize and approach data protection and compliance. ',
		courses: [
			{
				id: 1,
				title: 'Data Security',
				tag: 'InfoSec',
				image: course4
			},
			{
				id: 2,
				title: 'Compliance',
				tag: 'InfoSec',
				image: course3
			}
		]
	}
];

export const IndividualCoursesData = [
	{
		icon: <Icon type="arrowLeft" role="img" />,
		title: 'Individual Courses',
		courses: [
			{
				id: 1,
				title: 'Phishing Simulation Training',
				courseCount: '4 courses',
				image: course1
			},
			{
				id: 2,
				title: 'Hotel Booking Scam Awareness',
				courseCount: '3 courses',
				image: course2
			},
			{
				id: 3,
				title: 'Advanced Learning Path',
				courseCount: '5 courses',
				image: course3,
				isMandatory: true
			},
			{
				id: 4,
				title: 'Information Security Breach',
				courseCount: '5 courses',
				image: course4
			},
			{
				id: 5,
				title: 'Booking Hotel Scam',
				courseCount: '5 courses',
				image: course3
			},
			{
				id: 6,
				title: 'Urgent CEO Request',
				courseCount: '5 courses',
				image: course2
			}
		]
	},
	{
		icon: <Icon type="arrowLeft" role="img" />,
		title: 'Intermediate Learning Path',
		duration: '40 min',
		description:
			'Deeper training covering breaches, policies, and organizational challenges.',
		courses: [
			{
				id: 1,
				title: 'Information Security Breach',
				tag: 'InfoSec',
				image: course4
			},
			{
				id: 2,
				title: 'Booking Hotel Scam',
				tag: 'InfoSec',
				image: course3
			},
			{
				id: 3,
				title: 'Urgent CEO Request',
				tag: 'InfoSec',
				image: course2,
				isMandatory: true
			},
			{
				id: 4,
				title: 'Urgent CEO Request',
				tag: 'InfoSec',
				image: course1
			}
		]
	}
];

export const courseDetailMockData: CourseDetailModalProps = {
	title: 'Fake Login Page',
	courseId: '1234',
	description:
		'An AI-powered phishing simulation that creates realistic fake login pages to test user awareness harvesting attempts.',
	runTime: '60 minutes',
	created: '2024',
	languages: [
		{ label: 'English' },
		{ label: 'Spanish' },
		{ label: 'Chinese' },
		{ label: 'Italian' },
		{ label: 'French' },
		{ label: 'German' }
	],
	courses: [
		{
			id: '1',
			number: 1,
			title: 'Information security breaches',
			subtitle: 'Security awareness',
			description:
				'An AI-powered phishing simulation that creates realistic fake login pages to test user awareness harvesting attempts.',
			runTime: '20 minutes',
			created: '2024',
			languages: [
				{ label: 'English' },
				{ label: 'Spanish' },
				{ label: 'Chinese' },
				{ label: 'Italian' },
				{ label: 'French' },
				{ label: 'German' }
			]
		},
		{
			id: '2',
			number: 2,
			title: 'Information security policies',
			subtitle: 'AI-Driven',
			description:
				'An AI-powered phishing simulation that creates realistic fake login pages to test user awareness harvesting attempts.',
			runTime: '20 minutes',
			created: '2024',
			languages: [
				{ label: 'English' },
				{ label: 'Spanish' },
				{ label: 'Chinese' },
				{ label: 'Italian' },
				{ label: 'French' },
				{ label: 'German' }
			]
		},
		{
			id: '3',
			number: 3,
			title: 'PCI DSS Expectations for leaders',
			subtitle: 'AI-Driven',
			description:
				'An AI-powered phishing simulation that creates realistic fake login pages to test user awareness harvesting attempts.',
			runTime: '20 minutes',
			created: '2024',
			languages: [
				{ label: 'English' },
				{ label: 'Spanish' },
				{ label: 'Chinese' },
				{ label: 'Italian' },
				{ label: 'French' },
				{ label: 'German' }
			]
		},
		{
			id: '4',
			number: 4,
			title: 'Vulnerability and endor management',
			subtitle: 'AI-Driven',
			description:
				'An AI-powered phishing simulation that creates realistic fake login pages to test user awareness harvesting attempts.',
			runTime: '20 minutes',
			created: '2024',
			languages: [
				{ label: 'English' },
				{ label: 'Spanish' },
				{ label: 'Chinese' },
				{ label: 'Italian' },
				{ label: 'French' },
				{ label: 'German' }
			]
		}
	]
};

export interface OptionProps {
	id: number;
	icon: React.ReactNode;
	title: string;
	desc: string;
	component: React.ReactNode;
}
export interface OptionCardProps extends OptionProps {
	selectedId: number | null;
	toggleSelect: (id: number) => void;
}

export const options: OptionProps[] = [
	{
		id: 214,
		icon: <Icon type="orgIcon" />,
		title: 'By Organization',
		desc: 'Send to all users in your organization',
		component: <Organization />
	},
	{
		id: 35,
		icon: <Icon type="propIcon" />,
		title: 'By Property',
		desc: 'Select entire properties',
		component: <Property />
	},
	{
		id: 634,
		icon: <Icon type="roleIcon" />,
		title: 'By Role',
		desc: 'Send to all users assigned to a specific role',
		component: <Role />
	},
	{
		id: 676,
		icon: <Icon type="groupIcon" />,
		title: 'By Group',
		desc: 'Select groups or departments',
		component: <Group />
	},
	{
		id: 784,
		icon: <Icon type="individualIcon" />,
		title: 'By Individual',
		desc: 'Select specific individuals by name or email',
		component: <Individual />
	}
];
